import { useMemo } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { z } from 'zod';

import type { App } from '@keboola/api-client/developerPortal/types';
import {
  AlertModal,
  Badge,
  Button,
  EmptyState,
  FormGroup,
  Label,
  PageHeader,
  PageLayout,
  PageToolbar,
  SearchInput,
  Select,
  Skeleton,
  Table,
  toast,
} from '@keboola/design';

import { QueryError } from '../../components/QueryError';
import { PublishRejectActions } from '../../features/admin/components/PublishRejectActions';
import { useAdminApps, useAdminDeleteApp } from '../../features/admin/queries';
import { AppIcon } from '../../features/apps/components/AppIcon';
import { getComponentTypeLabel } from '../../features/apps/helpers';
import { getApiErrorMessage } from '../../features/auth/errors';

/** The visibility filter's URL values; "all" is the absent key. */
const VISIBILITY = ['public', 'private'] as const;

type Visibility = (typeof VISIBILITY)[number];

/** The `Select`'s "all" option; non-empty because `Select` treats `''` as no value. */
const ALL = 'all';

/** `Select` hands back a bare `string`; the search schema wants the narrowed union, or nothing for "all". */
const asVisibility = (value: string): Visibility | undefined =>
  (VISIBILITY as readonly string[]).includes(value) ? (value as Visibility) : undefined;

const searchSchema = z.object({
  query: z.string().optional(),
  status: z.enum(VISIBILITY).optional(),
});

export const Route = createFileRoute('/admin/')({
  // Filters live in the URL so a filtered list can be shared as a link.
  validateSearch: searchSchema,
  component: AdminAppsPage,
  head: () => ({ meta: [{ title: 'Components | Keboola Developer Portal admin' }] }),
});

const VISIBILITY_OPTIONS = [
  { value: ALL, label: 'All' },
  { value: 'public', label: 'Public' },
  { value: 'private', label: 'Private' },
];

/** Matches id, name, vendor or author. */
const matchesQuery = (app: App, needle: string): boolean =>
  [app.id, app.name, app.vendor.name ?? app.vendor.id, app.createdBy ?? ''].some((value) =>
    value.toLowerCase().includes(needle),
  );

function AdminAppsPage() {
  const { query = '', status } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { data: apps, isLoading, isError, error, refetch } = useAdminApps();
  const deleteApp = useAdminDeleteApp();

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (apps ?? [])
      .filter((app) => {
        if (status === 'public' && app.isPublic !== true) return false;
        if (status === 'private' && app.isPublic === true) return false;
        return needle === '' || matchesQuery(app, needle);
      })
      .sort((a, b) => a.id.localeCompare(b.id));
  }, [apps, query, status]);

  return (
    <PageLayout>
      <PageLayout.Header>
        <PageHeader.Index title="Components" description="Every component across all vendors." />
      </PageLayout.Header>

      {isLoading && <Skeleton className="tw:h-64" />}

      <QueryError
        isError={isError}
        hasData={apps !== undefined}
        error={error}
        onRetry={() => void refetch()}
        loadTitle="Could not load the components"
        refreshTitle="Could not refresh the components"
        staleNote="Showing the components from the last successful load."
        titleAs="h2"
      />

      {apps !== undefined && (
        <>
          <PageToolbar>
            {/* Labelled like the filter beside it so `items-center` aligns their baselines. */}
            <PageToolbar.Search>
              <FormGroup className="tw:w-full">
                <Label htmlFor="admin-search">Search</Label>
                <SearchInput
                  id="admin-search"
                  allowClear
                  placeholder="Search by ID, name, vendor or author"
                  value={query}
                  onValueChange={(next) =>
                    void navigate({ search: (prev) => ({ ...prev, query: next || undefined }) })
                  }
                />
              </FormGroup>
            </PageToolbar.Search>
            {/* `Label` is the trigger's only accessible name: `Select` takes no `aria-label`. */}
            <FormGroup className="tw:w-44">
              <Label htmlFor="admin-visibility">Visibility</Label>
              <Select
                id="admin-visibility"
                items={VISIBILITY_OPTIONS}
                value={status ?? ALL}
                onValueChange={(next) =>
                  void navigate({ search: (prev) => ({ ...prev, status: asVisibility(next) }) })
                }
              />
            </FormGroup>
          </PageToolbar>

          {visible.length === 0 ? (
            <EmptyState variant="compact">
              <EmptyState.Description>No component matches these filters.</EmptyState.Description>
            </EmptyState>
          ) : (
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.Head>Component</Table.Head>
                  <Table.Head>Type</Table.Head>
                  <Table.Head>Version</Table.Head>
                  <Table.Head>Publishing</Table.Head>
                  <Table.Head>Visibility</Table.Head>
                  <Table.Head>
                    <span className="tw:sr-only">Actions</span>
                  </Table.Head>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {visible.map((app) => (
                  <Table.Row key={app.id}>
                    <Table.Cell>
                      <div className="tw:flex tw:items-center tw:gap-3">
                        <AppIcon app={app} size={48} />
                        <div className="tw:flex tw:flex-col">
                          <Link
                            to="/admin/components/$appId"
                            params={{ appId: app.id }}
                            className="tw:font-medium"
                          >
                            {app.name}
                          </Link>
                          <span className="tw:text-xs tw:text-muted-foreground">{app.id}</span>
                          <span className="tw:text-xs tw:text-muted-foreground">
                            {app.vendor.name ?? app.vendor.id}
                            {app.createdBy !== undefined && ` · ${app.createdBy}`}
                          </span>
                        </div>
                      </div>
                    </Table.Cell>
                    <Table.Cell>{getComponentTypeLabel(app.type)}</Table.Cell>
                    <Table.Cell>{app.version ?? '—'}</Table.Cell>
                    <Table.Cell>
                      <div className="tw:flex tw:flex-col tw:gap-1.5">
                        <PublishingCell app={app} />
                        <PublishRejectActions app={app} />
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      {app.isPublic === true ? (
                        <Badge color="green-inverse">Public</Badge>
                      ) : app.isDeprecated === true ? (
                        <Badge color="red">Deprecated</Badge>
                      ) : (
                        <Badge color="gray-inverse">Private</Badge>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <div className="tw:flex tw:justify-end tw:gap-2">
                        <Button asChild variant="secondary" size="small">
                          <Link to="/admin/components/$appId/edit" params={{ appId: app.id }}>
                            Edit
                          </Link>
                        </Button>
                        <Button asChild variant="invisible" size="small">
                          <Link to="/admin/components/$appId/changes" params={{ appId: app.id }}>
                            Changes
                          </Link>
                        </Button>
                        <AlertModal
                          // Caught: `AlertModal` closes on settle, so a rejection would vanish silently.
                          onConfirm={async () => {
                            try {
                              // Vendor-scoped route: apps-api has no admin DELETE.
                              await deleteApp.mutateAsync({
                                vendorId: app.vendor.id,
                                appId: app.id,
                              });
                              toast.success(`${app.name} was removed.`);
                            } catch (error) {
                              toast.error(
                                getApiErrorMessage(error, `Could not remove ${app.name}.`),
                              );
                            }
                          }}
                        >
                          <AlertModal.Trigger asChild>
                            <Button variant="danger" size="small">
                              Remove
                            </Button>
                          </AlertModal.Trigger>
                          <AlertModal.Content
                            variant="danger"
                            title={`Remove ${app.name}?`}
                            confirmLabel="Remove component"
                          >
                            This cannot be undone. Any project using this component will stop being
                            able to run it.
                          </AlertModal.Content>
                        </AlertModal>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </>
      )}
    </PageLayout>
  );
}

/** The publishing request's state, and when it was filed. */
function PublishingCell({ app }: { app: App }) {
  if (app.publishingStatus === 'requested') {
    const requestedOn = app.publishingRequest?.createdOn;
    return (
      <div className="tw:flex tw:flex-col tw:gap-0.5">
        <Badge color="amber">Requested</Badge>
        {requestedOn !== undefined && requestedOn !== null && (
          <span className="tw:text-xs tw:text-muted-foreground">
            {new Date(requestedOn).toLocaleDateString()}
          </span>
        )}
      </div>
    );
  }

  if (app.publishingStatus === 'rejected') {
    const reason = app.publishingRequest?.rejectionReason;
    return (
      <div className="tw:flex tw:flex-col tw:gap-0.5">
        <Badge color="red">Rejected</Badge>
        {typeof reason === 'string' && reason !== '' && (
          <span className="tw:text-xs tw:text-muted-foreground">{reason}</span>
        )}
      </div>
    );
  }

  return <span className="tw:text-muted-foreground">—</span>;
}
